class StickyAddToCart {
  constructor(bar) {
    this.bar = bar;
    this.productInfo = document.querySelector('product-info');
    this.sectionId = this.productInfo?.dataset.section;
    this.image = bar.querySelector('.sticky-atc__image');
    this.price = bar.querySelector('.sticky-atc__price');
    this.submitButton = bar.querySelector('.sticky-atc__submit');
    this.submitLabel = bar.querySelector('.sticky-atc__submit-label');
    this.supportButton = bar.querySelector('.sticky-atc__support');
    this.mainSubmitButton = null;
    this.mainForm = null;
    this.variantUnsubscriber = undefined;

    this.init();
  }

  init() {
    // If sectionId is missing, fallback to querying the first available product submit button
    if (this.sectionId) {
      this.mainSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    }
    
    if (!this.mainSubmitButton) {
      this.mainSubmitButton = document.querySelector('product-form [type="submit"], form[action*="/cart/add"] [type="submit"]');
    }

    if (!this.mainSubmitButton) return;

    this.mainForm = this.mainSubmitButton.closest('form');

    this.bindEvents();
    this.observeMainButton();
    this.syncFromMainButton();
    this.setBarHeight();
    window.addEventListener('resize', this.setBarHeight.bind(this));
  }

  bindEvents() {
    this.submitButton?.addEventListener('click', this.onSubmitClick.bind(this));
    this.supportButton?.addEventListener('click', this.openSupportChat.bind(this));

    // Dawn PUB_SUB listeners with safety check
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (this.sectionId && event.data.sectionId !== this.sectionId) return;
        this.updateFromVariant(event.data.variant);
      });

      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.setLoading(false);
      });

      subscribe(PUB_SUB_EVENTS.cartError, (event) => {
        this.setLoading(false);
      });
    }

    // Fallback: Listen to custom option changes or native select change
    const variantSelect = document.querySelector('select[name="id"]');
    if (variantSelect) {
      variantSelect.addEventListener('change', () => {
        setTimeout(() => this.syncFromMainButton(), 150);
      });
    }
  }

  observeMainButton() {
    if (!this.mainSubmitButton) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        this.bar.classList.toggle('hidden', isVisible);
        this.bar.setAttribute('aria-hidden', isVisible ? 'true' : 'false');
        document.body.classList.toggle('sticky-atc-visible', !isVisible);
        this.setBarHeight();
      },
      { root: null, threshold: 0 }
    );

    observer.observe(this.mainSubmitButton);
  }

  onSubmitClick(e) {
    e.preventDefault();

    if (!this.mainSubmitButton || this.mainSubmitButton.disabled || this.submitButton.disabled) return;

    this.setLoading(true);

    // 1. First attempt: Trigger main button click (handles app validations & Dawn AJAX handlers)
    this.mainSubmitButton.click();

    // 2. Fallback: If form does not trigger within 400ms, force requestSubmit
    setTimeout(() => {
      const isStillLoading = this.submitButton.classList.contains('loading');
      if (isStillLoading && this.mainForm && typeof this.mainForm.requestSubmit === 'function') {
        try {
          this.mainForm.requestSubmit(this.mainSubmitButton);
        } catch (err) {
          this.setLoading(false);
        }
      }
    }, 400);

    // 3. Safety timeout to unlock button state if no events respond
    setTimeout(() => {
      this.setLoading(false);
    }, 3000);
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.classList.toggle('loading', isLoading);
    this.submitButton.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !isLoading);
    this.submitButton.disabled = isLoading || (this.mainSubmitButton ? this.mainSubmitButton.disabled : false);
  }

  syncFromMainButton() {
    if (!this.mainSubmitButton) return;

    const syncState = () => {
      this.submitButton.disabled = this.mainSubmitButton.disabled;
      const text = this.mainSubmitButton.querySelector('span')?.textContent?.trim() || this.mainSubmitButton.textContent?.trim();
      if (text && this.submitLabel) this.submitLabel.textContent = text;
    };

    syncState();

    const observer = new MutationObserver(syncState);
    observer.observe(this.mainSubmitButton, {
      attributes: true,
      attributeFilter: ['disabled', 'class'],
      childList: true,
      subtree: true,
    });
  }

  updateFromVariant(variant) {
    if (!variant) return;

    if (variant.featured_image?.src && this.image) {
      this.image.src = `${variant.featured_image.src}&width=100`;
      if (variant.featured_image.alt) this.image.alt = variant.featured_image.alt;
    }

    if (this.price) this.price.textContent = this.formatMoney(variant.price);
    if (this.submitButton) this.submitButton.disabled = !variant.available;
  }

  formatMoney(cents) {
    const moneyFormat = window.stickyAtcMoneyFormat || '${{amount}}';

    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    return (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    });
  }

  setBarHeight() {
    if (!this.bar.classList.contains('hidden')) {
      document.documentElement.style.setProperty('--sticky-atc-height', `${this.bar.offsetHeight}px`);
    }
  }

  openSupportChat() {
    if (window.Shopify?.Chat?.open) {
      window.Shopify.Chat.open();
      return;
    }

    const chatButton = document.querySelector(
      '#shopify-chat-button, [data-shopify-chat-button], .shopify-chat-button, #ShopifyChat-is-launched'
    );

    if (chatButton) {
      chatButton.click();
      return;
    }

    window.location.href = '/pages/contact';
  }

  destroy() {
    if (this.variantUnsubscriber) this.variantUnsubscriber();
  }
}

// Resilient initialization that works even if DOMContentLoaded already fired
function initStickyAddToCart() {
  const bar = document.getElementById('sticky-atc-bar');
  if (bar && !bar.dataset.initialized) {
    bar.dataset.initialized = 'true';
    new StickyAddToCart(bar);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickyAddToCart);
} else {
  initStickyAddToCart();
}